// EPIC-P05-07: Badge Detail Drawer Component
// Shows badge details, audit history, and actions

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { BadgeImage } from '@/components/profile/BadgeImage'
import { Badge } from '@/components/ui/badge'
import { AdminCard } from '../primitives'
import { getBadgeDetail, updateBadge } from '@/lib/api/admin/badges'
import { BadgeForm } from './BadgeForm'
import { UserBadgeList } from './UserBadgeList'
import { Edit, Copy, Power, Loader2, History } from 'lucide-react'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface BadgeDetailDrawerProps {
  badgeId: string
  open: boolean
  onClose: () => void
}

export function BadgeDetailDrawer({ badgeId, open, onClose }: BadgeDetailDrawerProps) {
  const [showEditForm, setShowEditForm] = useState(false)
  const [showUsers, setShowUsers] = useState(false)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-badge-detail', badgeId],
    queryFn: () => getBadgeDetail(badgeId),
    enabled: open && !!badgeId,
  })

  const badge = data?.badge
  const auditHistory = data?.audit_history || []

  // Debug logging
  if (badge) {
    console.log('[BadgeDetailDrawer] Badge data:', {
      id: badge.id,
      icon_path: badge.icon_path,
      icon: badge.icon,
      category: badge.category,
      achievement_id: badge.id,
      allKeys: Object.keys(badge),
    })
  }

  const handleClone = () => {
    if (!badge) return
    // Clone badge by opening create form with badge data
    setShowEditForm(true)
  }

  const handleDeactivate = async () => {
    if (!badge) return
    
    if (!confirm(`Are you sure you want to ${badge.is_active ? 'deactivate' : 'activate'} this badge?`)) {
      return
    }

    try {
      await updateBadge({
        id: badge.id,
        is_active: !badge.is_active,
      })
      toast.success(`Badge ${badge.is_active ? 'deactivated' : 'activated'} successfully`)
      refetch()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update badge')
    }
  }

  const handleEditSuccess = () => {
    setShowEditForm(false)
    refetch()
  }

  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="right" className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Loading Badge Details</SheetTitle>
            <SheetDescription>Please wait while we load the badge information</SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  if (error || !badge) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="right" className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Error Loading Badge</SheetTitle>
            <SheetDescription>Unable to load badge details</SheetDescription>
          </SheetHeader>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {error instanceof Error ? error.message : 'Failed to load badge details'}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Badge Details</SheetTitle>
            <SheetDescription>View and manage badge configuration</SheetDescription>
          </SheetHeader>

          <div className="space-y-6 mt-6">
            {/* Badge Preview */}
            <AdminCard accentColor="primary">
              <div className="flex items-start gap-4">
                <BadgeImage
                  category={badge.category}
                  rarity={badge.rarity}
                  badge_key={badge.badge_key}
                  achievement_id={badge.id}
                  image_path={badge.icon_path || null}
                  size={80}
                />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">{badge.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{badge.description}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{badge.category}</Badge>
                    <Badge>{badge.rarity}</Badge>
                    <Badge variant={badge.is_active ? 'default' : 'secondary'}>
                      {badge.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
            </AdminCard>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowEditForm(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={handleClone}>
                <Copy className="h-4 w-4 mr-2" />
                Clone
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeactivate}
              >
                <Power className="h-4 w-4 mr-2" />
                {badge.is_active ? 'Deactivate' : 'Activate'}
              </Button>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="details" className="w-full">
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="audit">Audit History</TabsTrigger>
                <TabsTrigger value="users">Users ({badge.id})</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4">
                <AdminCard accentColor="info" title="Badge Information">
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Badge Key</dt>
                      <dd className="text-sm font-mono">{badge.badge_key}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                      <dd className="text-sm">
                        {formatDistanceToNow(new Date(badge.created_at), { addSuffix: true })}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Last Updated</dt>
                      <dd className="text-sm">
                        {formatDistanceToNow(new Date(badge.updated_at), { addSuffix: true })}
                      </dd>
                    </div>
                    {badge.icon_path && (
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Icon Path</dt>
                        <dd className="text-sm font-mono">{badge.icon_path}</dd>
                      </div>
                    )}
                  </dl>
                </AdminCard>

                <AdminCard accentColor="info" title="Criteria">
                  <pre className="text-xs bg-muted p-4 rounded overflow-auto">
                    {JSON.stringify(badge.criteria, null, 2)}
                  </pre>
                </AdminCard>
              </TabsContent>

              <TabsContent value="audit" className="space-y-4">
                <AdminCard accentColor="info" title="Audit History">
                  {auditHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No audit history available
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {auditHistory.map((entry: any) => (
                        <div key={entry.id} className="border-l-2 border-primary pl-4 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium capitalize">{entry.action}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          {entry.profiles && (
                            <p className="text-xs text-muted-foreground">
                              by {entry.profiles.email || entry.profiles.full_name || 'Unknown'}
                            </p>
                          )}
                          {entry.details && Object.keys(entry.details).length > 0 && (
                            <pre className="text-xs bg-muted p-2 rounded mt-2 overflow-auto">
                              {JSON.stringify(entry.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </AdminCard>
              </TabsContent>

              <TabsContent value="users">
                <UserBadgeList badgeId={badgeId} />
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Form */}
      {showEditForm && (
        <BadgeForm
          open={showEditForm}
          onClose={() => setShowEditForm(false)}
          onSuccess={handleEditSuccess}
          badge={badge}
        />
      )}
    </>
  )
}

